/**
 * Signup-page attribution.
 *
 * FB landings set signupSource from the landing path. FB4 (and any flow that
 * signs up on `/signup` after browsing the landing) uses sessionStorage so the
 * last FB landing in the tab wins until the account is created.
 *
 *   /fb1 → fb1
 *   /fb2 → fb2
 *   /fb3 → fb3
 *   /fb4 → fb4
 *   homepage and every other path → direct
 */

export type SignupSource = "fb1" | "fb2" | "fb3" | "fb4" | "direct";

const FB_LANDING_SESSION_KEY = "rc.fbLandingAttribution";

type StoredFbLanding = {
  source: Exclude<SignupSource, "direct">;
  path: string;
};

export function normalizeSignupPath(pathname: string | undefined | null): string | undefined {
  if (!pathname) return undefined;
  const path = pathname.split("?")[0].split("#")[0].toLowerCase();
  return path.replace(/\/+$/, "") || "/";
}

export function signupSourceFromPath(pathname: string | undefined | null): SignupSource {
  const path = normalizeSignupPath(pathname);
  if (path === "/fb1") return "fb1";
  if (path === "/fb2") return "fb2";
  if (path === "/fb3") return "fb3";
  if (path === "/fb4") return "fb4";
  return "direct";
}

function readStoredFbLanding(): StoredFbLanding | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(FB_LANDING_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredFbLanding;
    if (
      parsed?.source !== "fb1" &&
      parsed?.source !== "fb2" &&
      parsed?.source !== "fb3" &&
      parsed?.source !== "fb4"
    ) {
      return null;
    }
    if (typeof parsed.path !== "string" || !parsed.path.startsWith("/fb")) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredFbLanding(entry: StoredFbLanding): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(FB_LANDING_SESSION_KEY, JSON.stringify(entry));
  } catch {
    // sessionStorage may be unavailable (private mode, quota).
  }
}

/** Persist FB landing attribution for the current tab (last landing wins). */
export function rememberFbLandingFromPath(pathname: string | undefined | null): void {
  const path = normalizeSignupPath(pathname);
  const source = signupSourceFromPath(path);
  if (source === "direct" || !path) return;
  writeStoredFbLanding({ source, path });
}

/** Source used when creating a new account (current FB page or remembered landing). */
export function currentSignupSource(): SignupSource {
  if (typeof window === "undefined") return "direct";
  const path = normalizeSignupPath(window.location.pathname);
  const fromPath = signupSourceFromPath(path);
  if (fromPath !== "direct") return fromPath;
  return readStoredFbLanding()?.source ?? "direct";
}

/** Path recorded on signup (FB landing path when attributed, else current path). */
export function currentSignupPath(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const path = normalizeSignupPath(window.location.pathname);
  const fromPath = signupSourceFromPath(path);
  if (fromPath !== "direct" && path) return path;
  const stored = readStoredFbLanding();
  if (stored) return stored.path;
  return window.location.pathname;
}

/** Active FB campaign label for Meta Pixel `content_category`, if any. */
export function activeFbSignupSourceForPixel(): Exclude<SignupSource, "direct"> | undefined {
  const source = currentSignupSource();
  return source === "direct" ? undefined : source;
}
